import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Play } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { formatDateTime } from "@/lib/format";
import {
  DEFAULT_MSG_TO_AUTH_DATA,
  kbCaasActivateByKbk,
  kbCaasAuthorizeTransaction,
  kbCaasCancel,
  kbCaasContinue,
  kbCaasLogin,
  kbCaasRegisterRos,
  searchKbCaasStandaloneTokens,
  useKbCaasSessions,
  type KbCaasEnvelope,
  type KbCaasRosResult,
  type KbCaasStatus,
} from "@/lib/kbCaasWorkflows";
import { Field, inputCls } from "../shared";
import { TokenPicker } from "@/components/app/TokenPicker";
import { Table } from "@/components/application/table/table";

type Wf = "activate" | "login" | "ros" | "authorize" | "sessions";

function statusColor(status?: KbCaasStatus): "success" | "error" | "warning" | "blue" | "gray" {
  if (status === "COMPLETED") return "success";
  if (status === "FAILED" || status === "BLOCKED") return "error";
  if (status === "NEEDS_OTP" || status === "NEEDS_OIDC_REDIRECT") return "warning";
  if (status === "IN_PROGRESS") return "blue";
  return "gray";
}

/**
 * KB CAAS Authorization Workflows — interaktivní multi-step panel (jen kb-caas
 * tab). 5 workflow operací + session continuation. Port kbcaasAuth sekce.
 */
export function KbCaasWorkflowsPanel() {
  const { t } = useTranslation();
  const [wf, setWf] = useState<Wf>("activate");
  const [envelope, setEnvelope] = useState<KbCaasEnvelope | null>(null);
  const [rosResult, setRosResult] = useState<KbCaasRosResult | null>(null);
  const [lastRos, setLastRos] = useState<{ requestUri: string; kbSessionId: string } | null>(null);
  const [running, setRunning] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Form values
  const [actKbk, setActKbk] = useState({ kbkTokenId: "", useValidPin: "valid", language: "cs_CZ", note: "" });
  const [login, setLogin] = useState({ kbkTokenId: "", useValidPin: "valid" });
  const [ros, setRos] = useState({ authSubject: "970878519", msgToAuthData: DEFAULT_MSG_TO_AUTH_DATA, scoringData: "" });
  const [tx, setTx] = useState({ paatTokenId: "", requestUri: "", kbSessionId: "", useValidPin: "valid" });

  // Continuation
  const [cont, setCont] = useState({ sessionId: "", otp: "", oidcUrl: "" });
  const [cancelArmed, setCancelArmed] = useState(false);

  const sessions = useKbCaasSessions(wf === "sessions");

  const afterEnvelope = (env: KbCaasEnvelope) => {
    setEnvelope(env);
    if (env.sessionId) setCont((c) => ({ ...c, sessionId: env.sessionId! }));
  };

  const guard = (cond: boolean, msg: string) => {
    if (!cond) {
      setValidationError(msg);
      return false;
    }
    return true;
  };

  const run = async () => {
    setValidationError(null);
    setEnvelope(null);
    setRosResult(null);
    setRunning(true);
    try {
      if (wf === "activate") {
        if (!guard(!!actKbk.kbkTokenId, t("apitools.required", { field: t("kbcaasauth.f.kbkToken") }))) return;
        afterEnvelope(
          await kbCaasActivateByKbk({
            kbkTokenId: actKbk.kbkTokenId,
            useValidPin: actKbk.useValidPin === "valid",
            language: actKbk.language || undefined,
            note: actKbk.note || undefined,
          }),
        );
      } else if (wf === "login") {
        if (!guard(!!login.kbkTokenId, t("apitools.required", { field: t("kbcaasauth.f.kbkToken") }))) return;
        afterEnvelope(await kbCaasLogin({ kbkTokenId: login.kbkTokenId, useValidPin: login.useValidPin === "valid" }));
      } else if (wf === "ros") {
        if (!guard(!!ros.authSubject.trim(), t("apitools.required", { field: t("kbcaasauth.f.authSubject") }))) return;
        let msgToAuthData: unknown;
        try {
          msgToAuthData = JSON.parse(ros.msgToAuthData);
        } catch (e) {
          setValidationError(`msgToAuth.data: ${(e as Error).message}`);
          return;
        }
        const r = await kbCaasRegisterRos({
          authSubject: ros.authSubject,
          msgToAuthData,
          scoringData: ros.scoringData || undefined,
        });
        setRosResult(r);
        if (r.requestUri) setLastRos({ requestUri: r.requestUri, kbSessionId: r.kbSessionId ?? "" });
      } else if (wf === "authorize") {
        if (!guard(!!tx.paatTokenId, t("apitools.required", { field: t("kbcaasauth.f.paatToken") }))) return;
        if (!guard(!!tx.requestUri.trim(), t("apitools.required", { field: t("kbcaasauth.f.requestUri") }))) return;
        if (!guard(!!tx.kbSessionId.trim(), t("apitools.required", { field: t("kbcaasauth.f.kbSessionId") }))) return;
        afterEnvelope(
          await kbCaasAuthorizeTransaction(
            { paatTokenId: tx.paatTokenId, requestUri: tx.requestUri, useValidPin: tx.useValidPin === "valid" },
            tx.kbSessionId.trim(),
          ),
        );
      }
    } catch (e) {
      setEnvelope({ status: "FAILED", error: { message: (e as Error).message } });
    } finally {
      setRunning(false);
    }
  };

  const copyRosToTx = () => {
    if (!lastRos) return;
    setTx((s) => ({ ...s, requestUri: lastRos.requestUri, kbSessionId: lastRos.kbSessionId }));
    setWf("authorize");
  };

  const doContinue = async () => {
    if (!cont.sessionId.trim()) return;
    setRunning(true);
    try {
      const body: Record<string, unknown> = {};
      if (cont.otp.trim()) body.otp = cont.otp.trim();
      if (cont.oidcUrl.trim()) body.oidcRedirectResultUrl = cont.oidcUrl.trim();
      afterEnvelope(await kbCaasContinue(cont.sessionId.trim(), body));
    } catch (e) {
      setEnvelope({ status: "FAILED", error: { message: (e as Error).message } });
    } finally {
      setRunning(false);
    }
  };

  const doCancel = async () => {
    if (!cont.sessionId.trim()) return;
    if (!cancelArmed) {
      setCancelArmed(true);
      return;
    }
    setCancelArmed(false);
    setRunning(true);
    try {
      afterEnvelope(await kbCaasCancel(cont.sessionId.trim()));
    } finally {
      setRunning(false);
    }
  };

  const needsContinuation = envelope?.status === "NEEDS_OTP" || envelope?.status === "NEEDS_OIDC_REDIRECT";

  const WF_TABS: { id: Wf; label: string }[] = [
    { id: "activate", label: "kbcaasauth.wf.activate" },
    { id: "login", label: "kbcaasauth.wf.login" },
    { id: "ros", label: "kbcaasauth.wf.ros" },
    { id: "authorize", label: "kbcaasauth.wf.authorize" },
    { id: "sessions", label: "kbcaasauth.wf.sessions" },
  ];

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="text-md font-semibold text-primary">{t("kbcaasauth.title")}</h2>

      {/* Workflow sub-tabs */}
      <div className="flex flex-wrap gap-1">
        {WF_TABS.map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() => setWf(x.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              wf === x.id ? "bg-brand-solid text-white" : "text-secondary hover:bg-primary_hover"
            }`}
          >
            {t(x.label)}
          </button>
        ))}
      </div>

      {wf === "sessions" ? (
        <SessionsTable
          sessions={sessions.data ?? []}
          loading={sessions.isLoading}
          onRefresh={() => void sessions.refetch()}
          onUse={(id) => setCont((c) => ({ ...c, sessionId: id }))}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Forms */}
          <div className="flex flex-col gap-4">
            {wf === "activate" && (
              <>
                <TokenSelect label={`${t("kbcaasauth.f.kbkToken")} *`} value={actKbk.kbkTokenId} standaloneOnly onChange={(v) => setActKbk((s) => ({ ...s, kbkTokenId: v }))} />
                <PinRadio value={actKbk.useValidPin} onChange={(v) => setActKbk((s) => ({ ...s, useValidPin: v }))} />
                <Field label={t("kbcaasauth.f.language")}>
                  <input className={inputCls} value={actKbk.language} onChange={(e) => setActKbk((s) => ({ ...s, language: e.target.value }))} />
                </Field>
                <Field label={t("kbcaasauth.f.note")}>
                  <input className={inputCls} value={actKbk.note} onChange={(e) => setActKbk((s) => ({ ...s, note: e.target.value }))} />
                </Field>
              </>
            )}

            {wf === "login" && (
              <>
                <TokenSelect label={`${t("kbcaasauth.f.kbkToken")} *`} value={login.kbkTokenId} onChange={(v) => setLogin((s) => ({ ...s, kbkTokenId: v }))} />
                <PinRadio value={login.useValidPin} onChange={(v) => setLogin((s) => ({ ...s, useValidPin: v }))} />
              </>
            )}

            {wf === "ros" && (
              <>
                <Field label={`${t("kbcaasauth.f.authSubject")} *`}>
                  <input className={inputCls} value={ros.authSubject} placeholder="970878519" onChange={(e) => setRos((s) => ({ ...s, authSubject: e.target.value }))} />
                </Field>
                <Field label={`${t("kbcaasauth.f.msgToAuthData")} *`}>
                  <textarea className={`${inputCls} h-72 font-mono`} value={ros.msgToAuthData} onChange={(e) => setRos((s) => ({ ...s, msgToAuthData: e.target.value }))} />
                </Field>
                <Field label={t("kbcaasauth.f.scoringData")}>
                  <input className={inputCls} value={ros.scoringData} onChange={(e) => setRos((s) => ({ ...s, scoringData: e.target.value }))} />
                </Field>
              </>
            )}

            {wf === "authorize" && (
              <>
                <TokenSelect label={`${t("kbcaasauth.f.paatToken")} *`} value={tx.paatTokenId} onChange={(v) => setTx((s) => ({ ...s, paatTokenId: v }))} />
                <Field label={`${t("kbcaasauth.f.requestUri")} *`}>
                  <input className={inputCls} value={tx.requestUri} placeholder="urn:uuid:…" onChange={(e) => setTx((s) => ({ ...s, requestUri: e.target.value }))} />
                </Field>
                <Field label={`${t("kbcaasauth.f.kbSessionId")} *`}>
                  <input className={inputCls} value={tx.kbSessionId} onChange={(e) => setTx((s) => ({ ...s, kbSessionId: e.target.value }))} />
                </Field>
                <PinRadio value={tx.useValidPin} onChange={(v) => setTx((s) => ({ ...s, useValidPin: v }))} />
              </>
            )}

            {validationError && <p className="text-sm text-error-primary">{validationError}</p>}

            <div>
              <Button size="lg" iconLeading={Play} isDisabled={running} onPress={() => void run()}>
                {running ? t("testing.running") : t("kbcaasauth.run")}
              </Button>
            </div>
          </div>

          {/* Result + ROS + continuation */}
          <div className="flex flex-col gap-4">
            {rosResult && <RosResultView ros={rosResult} onCopy={copyRosToTx} />}
            <EnvelopeView envelope={envelope} />
            {needsContinuation && (
              <ContinuationPanel
                cont={cont}
                setCont={setCont}
                onContinue={() => void doContinue()}
                onCancel={() => void doCancel()}
                cancelArmed={cancelArmed}
                running={running}
                needsOtp={envelope?.status === "NEEDS_OTP"}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Výběr tokenu pro workflow. `standaloneOnly` přepíná zdroj na KB seznam
 * (aktivní tokeny KB Klíče) — activate-by-KBK podepisuje vždy STANDALONE token,
 * NDB_SDK do něj nepatří.
 */
function TokenSelect({
  label,
  value,
  standaloneOnly = false,
  onChange,
}: {
  label: string;
  value: string;
  standaloneOnly?: boolean;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      <TokenPicker
        value={value}
        onChange={onChange}
        authServerType="KB_CAAS"
        activeOnly
        placeholder={t("apitools.selectToken")}
        search={standaloneOnly ? (q) => searchKbCaasStandaloneTokens(q) : undefined}
      />
    </Field>
  );
}

function PinRadio({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  return (
    <Field label={t("kbcaasauth.f.pinType")}>
      <div className="flex gap-4">
        {(["valid", "invalid"] as const).map((pt) => (
          <label key={pt} className="flex items-center gap-2 text-sm text-primary">
            <input type="radio" checked={value === pt} onChange={() => onChange(pt)} />
            {t(pt === "valid" ? "apitools.validPin" : "apitools.invalidPin")}
          </label>
        ))}
      </div>
    </Field>
  );
}

function RosResultView({ ros, onCopy }: { ros: KbCaasRosResult; onCopy: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 rounded-xl bg-secondary p-4 ring-1 ring-secondary">
      <h4 className="text-xs font-semibold text-tertiary">{t("kbcaasauth.rosResult")}</h4>
      <pre className="max-h-40 overflow-auto font-mono text-xs break-all text-secondary">{JSON.stringify(ros, null, 2)}</pre>
      {ros.requestUri && (
        <div>
          <Button size="sm" color="secondary" onPress={onCopy}>
            {t("kbcaasauth.copyToTx")}
          </Button>
        </div>
      )}
    </div>
  );
}

function EnvelopeView({ envelope }: { envelope: KbCaasEnvelope | null }) {
  const { t } = useTranslation();
  if (!envelope) {
    return (
      <div className="flex items-center justify-center rounded-xl bg-secondary p-5 text-sm text-tertiary ring-1 ring-secondary">
        {t("apitools.executeHint")}
      </div>
    );
  }
  const { status, sessionId, result, nextAction, error } = envelope;
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center gap-2">
        <Badge type="pill-color" size="sm" color={statusColor(status)}>
          {status}
        </Badge>
        {sessionId && <span className="font-mono text-xs text-tertiary">{sessionId.slice(0, 8)}…</span>}
      </div>

      {nextAction && (
        <div className="rounded-md bg-primary p-3 text-xs ring-1 ring-secondary">
          {nextAction.hint && <p className="text-secondary">{nextAction.hint}</p>}
          {nextAction.what && <p className="text-tertiary">what: {nextAction.what}</p>}
          {nextAction.retriesLeft != null && <p className="text-tertiary">{t("kbcaasauth.retriesLeft")}: {nextAction.retriesLeft}</p>}
          {nextAction.expiresAt && <p className="text-tertiary">{t("kbcaasauth.expiresAt")}: {nextAction.expiresAt}</p>}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-primary p-3 text-xs ring-1 ring-error">
          <p className="font-semibold text-error-primary">{error.code}</p>
          {error.message && <p className="text-secondary">{error.message}</p>}
          {error.recoveryHint && <p className="text-tertiary">{t("kbcaasauth.recoveryHint")}: {error.recoveryHint}</p>}
          {error.canRetry != null && <p className="text-tertiary">{t("kbcaasauth.canRetry")}: {String(error.canRetry)}</p>}
        </div>
      )}

      {result != null && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-tertiary">{t("apitools.response")}</h4>
          <pre className="max-h-72 overflow-auto rounded-md bg-primary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function ContinuationPanel({
  cont,
  setCont,
  onContinue,
  onCancel,
  cancelArmed,
  running,
  needsOtp,
}: {
  cont: { sessionId: string; otp: string; oidcUrl: string };
  setCont: React.Dispatch<React.SetStateAction<{ sessionId: string; otp: string; oidcUrl: string }>>;
  onContinue: () => void;
  onCancel: () => void;
  cancelArmed: boolean;
  running: boolean;
  needsOtp: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-primary p-4 ring-1 ring-warning">
      <h4 className="text-sm font-semibold text-primary">{t("kbcaasauth.continuation")}</h4>
      <Field label={t("kbcaasauth.f.sessionId")}>
        <input className={inputCls} value={cont.sessionId} onChange={(e) => setCont((c) => ({ ...c, sessionId: e.target.value }))} />
      </Field>
      {needsOtp ? (
        <Field label={t("kbcaasauth.f.otp")}>
          <input className={inputCls} value={cont.otp} placeholder="123456" onChange={(e) => setCont((c) => ({ ...c, otp: e.target.value }))} />
        </Field>
      ) : (
        <Field label={t("kbcaasauth.f.oidcUrl")}>
          <input className={inputCls} value={cont.oidcUrl} onChange={(e) => setCont((c) => ({ ...c, oidcUrl: e.target.value }))} />
        </Field>
      )}
      <div className="flex gap-2">
        <Button size="md" isDisabled={running} onPress={onContinue}>
          {t("kbcaasauth.continue")}
        </Button>
        <Button size="md" color="secondary-destructive" isDisabled={running} onPress={onCancel}>
          {cancelArmed ? t("kbcaasauth.cancelConfirm") : t("kbcaasauth.cancel")}
        </Button>
      </div>
    </div>
  );
}

function SessionsTable({
  sessions,
  loading,
  onRefresh,
  onUse,
}: {
  sessions: import("@/lib/kbCaasWorkflows").KbCaasSessionSummary[];
  loading: boolean;
  onRefresh: () => void;
  onUse: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button size="sm" color="secondary" onPress={onRefresh}>
          {t("kbcaasauth.sessions.load")}
        </Button>
      </div>
      {loading && <LoadingIndicator />}
      {!loading && sessions.length === 0 && <p className="text-sm text-tertiary">{t("kbcaasauth.sessions.empty")}</p>}
      {sessions.length > 0 && (
        <div className="overflow-hidden rounded-lg ring-1 ring-secondary">
          <Table aria-label={t("kbcaasauth.sessions.title")} selectionMode="none" size="sm">
            <Table.Header>
              <Table.Head isRowHeader id="sessionId" label={t("kbcaasauth.sessions.sessionId")} />
              <Table.Head id="workflow" label={t("kbcaasauth.sessions.workflow")} />
              <Table.Head id="status" label={t("kbcaasauth.sessions.status")} />
              <Table.Head id="template" label={t("kbcaasauth.sessions.template")} />
              <Table.Head id="lastStep" label={t("kbcaasauth.sessions.lastStep")} />
              <Table.Head id="actions" label="" />
            </Table.Header>
            <Table.Body items={sessions}>
              {(s) => (
                <Table.Row id={s.sessionId}>
                  <Table.Cell className="font-mono text-secondary">{s.sessionId.slice(0, 8)}…</Table.Cell>
                  <Table.Cell className="text-secondary">{s.workflow}</Table.Cell>
                  <Table.Cell>
                    <Badge type="pill-color" size="sm" color={statusColor(s.status)}>
                      {s.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-tertiary">{s.lastTemplate ?? "—"}</Table.Cell>
                  <Table.Cell className="text-tertiary">{s.lastStepAt ? formatDateTime(s.lastStepAt) : "—"}</Table.Cell>
                  <Table.Cell>
                    <div className="flex justify-end">
                      <Button size="sm" color="tertiary" onPress={() => onUse(s.sessionId)}>
                        {t("kbcaasauth.sessions.use")}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}
